import { useEffect, useCallback, useRef } from 'react';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { useDispatch, useSelector } from 'react-redux';

import { resetToastNotification } from '@/redux/action/ToastNotificationAction';
import { useMediaQuery } from '@shared/hooks/useMediaQuery';
import { scrollIntoViewInHost } from '@/utils/salesforceEmbedHost';

import toastErrorIcon from '@/assets/Images/toast-error.svg';

export type ToastType = 'none' | 'success' | 'error' | 'warning' | 'info';

export type Toast = {
  type: ToastType;
  message: string;
  persistent?: boolean;
  icon?: string;
  timestamp: number;
};

const ToastNotification = () => {
  const dispatch = useDispatch();
  const { toast } = useSelector((state: any) => state.toast);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    // The toast sits near the top of the page, which in the Salesforce embed can be far above
    // where the user is working; bring an error into view there. 'nearest' leaves a visible toast
    // alone.
    if (toast?.type === 'error') {
      const notification = containerRef.current?.querySelector('.k-notification');
      if (notification) {
        scrollIntoViewInHost(notification, { behavior: 'smooth', block: 'nearest' });
      }
    }
    if (toast) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = toast.persistent ? null : setTimeout(() => onClose(), 6000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ toast?.timestamp ]);

  const onClose = useCallback(() => {
    dispatch(resetToastNotification());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="notification-container" className="toast-notification" ref={containerRef}>
      <NotificationGroup
        style={{
          left: 10,
          right: 10,
          top: isMobile ? 70 : 80, // navbar height + 10px for offset (nav is 60px on mobile and 70px on desktop)
          alignItems: 'flex-end',
          zIndex: 99999,
          display: 'flex',
        }}
      >
        <Fade>
          { !!toast && (
            <Notification
              type={{ style: toast.type, icon: false }}
              closable
              onClose={onClose}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                {(toast.icon || toast.type === 'error') && (
                  <img
                    src={toast.icon || (toast.type === 'error' ? toastErrorIcon : '')}
                    alt="notification icon"
                    style={{
                      width: '16px',
                      height: '16px',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div dangerouslySetInnerHTML={{ __html: toast.message }}/>
              </div>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
    </div>
  );
};
export default ToastNotification;
